/**
 * Template Selector Component
 *
 * Allows users to select and apply saved import templates.
 */

import { useState } from 'react'
import { FileText, Check, Clock, Trash2, Loader2, Save } from 'lucide-react'
import { useCSVTemplates, useCSVTemplate, useDeleteCSVTemplate, useUpdateCSVTemplate } from '../hooks/csvImportQueries'
import type { ImportTemplateInfo, EntityLevel, ColumnMappingConfig } from '../types'

interface TemplateSelectorProps {
  entityLevel: EntityLevel
  selectedTemplate: ImportTemplateInfo | null
  currentMappings?: ColumnMappingConfig[]  // Current mappings for update
  onSelect: (template: ImportTemplateInfo, mappings: Record<string, ColumnMappingConfig>) => void
  onClear: () => void
  onUpdateSuccess?: () => void  // Callback after successful update
}

const ENTITY_LEVEL_LABELS: Record<EntityLevel, string> = {
  federal: 'Bund',
  state: 'Bundesland',
  government_district: 'Regierungsbezirk',
  county: 'Kreis',
  municipal_association: 'Gemeindeverband',
  municipality: 'Gemeinde',
}

export function TemplateSelector({
  entityLevel,
  selectedTemplate,
  currentMappings,
  onSelect,
  onClear,
  onUpdateSuccess,
}: TemplateSelectorProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [updateMessage, setUpdateMessage] = useState<string | null>(null)
  const [showAllTemplates, setShowAllTemplates] = useState(false)

  // Load templates - either filtered by entity level or all
  const { data: templates, isLoading: templatesLoading, refetch: refetchTemplates } = useCSVTemplates(
    showAllTemplates ? undefined : entityLevel
  )
  const { data: templateDetail, isLoading: detailLoading } = useCSVTemplate(selectedId)
  const deleteMutation = useDeleteCSVTemplate()
  const updateMutation = useUpdateCSVTemplate()

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate || !currentMappings || selectedTemplate.is_system) return

    // Convert array to object format for API
    const mappingsObj: Record<string, ColumnMappingConfig> = {}
    currentMappings.forEach((m) => {
      mappingsObj[m.csv_column] = {
        csv_column: m.csv_column,
        target: m.target,
        data_type: m.data_type,
        transform: m.transform ?? null,
        skip: m.skip,
      }
    })

    try {
      await updateMutation.mutateAsync({
        id: selectedTemplate.id,
        update: { column_mappings: mappingsObj },
      })
      setUpdateMessage('Template aktualisiert!')
      refetchTemplates()
      onUpdateSuccess?.()
      setTimeout(() => setUpdateMessage(null), 3000)
    } catch {
      setUpdateMessage('Fehler beim Aktualisieren')
      setTimeout(() => setUpdateMessage(null), 3000)
    }
  }

  const handleApply = () => {
    if (templateDetail) {
      onSelect(templateDetail, templateDetail.column_mappings)
      setSelectedId(null)
    }
  }

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Template wirklich loschen?')) {
      deleteMutation.mutate(id)
    }
  }

  if (templatesLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500 py-4 text-center">
          Keine Templates {showAllTemplates ? '' : `für ${ENTITY_LEVEL_LABELS[entityLevel]}`} verfügbar.
        </div>
        {!showAllTemplates && (
          <button
            onClick={() => setShowAllTemplates(true)}
            className="w-full text-sm text-blue-600 hover:text-blue-800"
          >
            Alle Templates anzeigen
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Filter Toggle */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {showAllTemplates ? 'Alle Templates' : `Templates für ${ENTITY_LEVEL_LABELS[entityLevel]}`}
        </span>
        <button
          onClick={() => setShowAllTemplates(!showAllTemplates)}
          className="text-blue-600 hover:text-blue-800"
        >
          {showAllTemplates ? 'Nur passende anzeigen' : 'Alle anzeigen'}
        </button>
      </div>

      {/* Template List */}
      <div className="border rounded-lg divide-y">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`p-3 flex items-center justify-between cursor-pointer transition-colors
              ${selectedId === template.id ? 'bg-blue-50' : 'hover:bg-gray-50'}
              ${selectedTemplate?.id === template.id ? 'bg-green-50 border-l-4 border-l-green-500' : ''}
            `}
            onClick={() => setSelectedId(selectedId === template.id ? null : template.id)}
          >
            <div className="flex items-center gap-3">
              <FileText className={`h-5 w-5 ${
                selectedTemplate?.id === template.id ? 'text-green-500' : 'text-gray-400'
              }`} />
              <div>
                <div className="font-medium text-gray-900 flex items-center gap-2">
                  {template.name}
                  {template.is_system && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                      System
                    </span>
                  )}
                  {selectedTemplate?.id === template.id && (
                    <Check className="h-4 w-4 text-green-500" />
                  )}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <span>{template.provider}</span>
                  <span className="text-gray-300">|</span>
                  <span>{ENTITY_LEVEL_LABELS[template.entity_level]}</span>
                  {template.last_used_at && (
                    <>
                      <span className="text-gray-300">|</span>
                      <Clock className="h-3 w-3" />
                      <span>
                        Zuletzt: {new Date(template.last_used_at).toLocaleDateString('de-DE')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!template.is_system && (
                <button
                  onClick={(e) => handleDelete(template.id, e)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Template loschen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Apply/Clear Buttons */}
      {selectedId && (
        <div className="flex items-center gap-2">
          <button
            onClick={handleApply}
            disabled={detailLoading}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700
              disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {detailLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Template anwenden
          </button>
        </div>
      )}

      {selectedTemplate && !selectedId && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 text-sm text-green-600 flex items-center gap-1">
              <Check className="h-4 w-4" />
              Template "{selectedTemplate.name}" angewendet
            </div>
            <button
              onClick={onClear}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Zurücksetzen
            </button>
          </div>

          {/* Update Template Button */}
          {!selectedTemplate.is_system && currentMappings && (
            <button
              onClick={handleUpdateTemplate}
              disabled={updateMutation.isPending}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-blue-300
                text-blue-700 rounded-lg hover:bg-blue-50 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Änderungen in Template speichern
            </button>
          )}

          {/* Update Message */}
          {updateMessage && (
            <div className={`text-sm text-center py-1 ${
              updateMessage.includes('Fehler') ? 'text-red-600' : 'text-green-600'
            }`}>
              {updateMessage}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
